import { File } from '@/js/utils/types/common.types'
import { ICompany } from '@/js/utils/types/company.types'
import { IHolding } from '@/js/utils/types/holding.types'
import store from 'store'

export type Role = 'admin' | 'holding' | 'collaborator' | 'company'

export type IUser = {
    accepted: boolean
    created_at: number
    email: string
    id: number
    updated_at: number
    image: null | File
    role: Role
} & (
    | {
          role: 'admin'
          surname?: string
          name?: string
      }
    | {
          role: 'collaborator'
          surname?: string
          name?: string
      }
    | {
          role: 'holding'
          business_name?: string
          companies?: ICompany[]
          holding_id?: number
      }
    | {
          role: 'company'
          business_name?: string
          company_id?: number
          holding?: IHolding
      }
)

export class User {
    accepted: boolean
    businessName?: string
    createdAt: number
    email: string
    id: number
    name?: string
    role: Role
    surname?: string
    updatedAt: number
    image: null | File
    companies?: ICompany[]
    companyId?: number
    holdingId?: number
    holding?: IHolding

    constructor(payload: IUser) {
        this.accepted = payload.accepted
        this.createdAt = payload.created_at
        this.email = payload.email
        this.id = payload.id
        this.role = payload.role
        this.updatedAt = payload.updated_at
        this.image = payload.image

        if (payload.role === 'admin' || payload.role === 'collaborator') {
            this.surname = payload.surname
            this.name = payload.name
        } else if (payload.role === 'holding') {
            this.businessName = payload.business_name
            this.companies = payload.companies
            this.holdingId = payload.holding_id
        } else {
            this.businessName = payload.business_name
            this.holding = payload.holding
            this.companyId = payload.company_id
        }
    }

    get isAdmin() {
        return this.role === 'admin'
    }

    get isHolding() {
        return this.role === 'holding'
    }

    get isCompany() {
        return this.role === 'company'
    }

    get isCollaborator() {
        return this.role === 'collaborator'
    }

    get hasPermissions() {
        return this.isAdmin || this.isCollaborator
    }

    get canSeeHoldings() {
        return this.isAdmin || this.isCollaborator
    }

    get getFullName() {
        return this.isAdmin
            ? this.name + ' ' + this.surname
            : this.isCollaborator
            ? this.name
                ? this.name + ' ' + this.surname
                : this.email
            : this.businessName
    }

    get editableCompanies() {
        return store.get('editable_companies_ids') as number[] | undefined
    }

    get canUploadConsumes() {
        return this.isAdmin || this.isCollaborator
    }

    canUploadConsumesByCompany(id: number | string) {
        if (!this.canUploadConsumes) return false

        if (this.isCollaborator) {
            const idInternal = typeof id === 'number' ? id : parseInt(id)
            return this.editableCompanies?.some((companyId) => companyId === idInternal) ?? false
        }

        return true
    }

    get canSeeEnpi() {
        return this.isAdmin || this.isCollaborator
    }

    get canManageEnpi() {
        return this.isAdmin
    }

    canSeeCompanyInfo(id: number | string) {
        if (this.isAdmin) return true

        if (this.isCollaborator) {
            const idInternal = typeof id === 'number' ? id : parseInt(id)
            return this.editableCompanies?.some((companyId) => companyId === idInternal) ?? false
        }

        return false
    }

    canSeeCompanySettings(id: number | string) {
        if (this.isAdmin) return true

        if (this.isCollaborator) {
            const idInternal = typeof id === 'number' ? id : parseInt(id)
            return this.editableCompanies?.some((companyId) => companyId === idInternal) ?? false
        }
    }

    get canSeeCompanyDetails() {
        return this.isAdmin || this.isCollaborator
    }

    get canSeeAllContractsList() {
        return this.isAdmin
    }

    get canManageHoldings() {
        return this.isAdmin
    }

    get canManageCollaborators() {
        return this.isAdmin
    }

    get canManageContracts() {
        return this.isAdmin || this.isCollaborator
    }

    canManageContractByCompany(id: number | string) {
        if (!this.canManageContracts) return false

        if (this.isCollaborator) {
            const idInternal = typeof id === 'number' ? id : parseInt(id)
            return this.editableCompanies?.some((companyId) => companyId === idInternal) ?? false
        }

        return true
    }

    get canManageCompanies() {
        return this.isAdmin || this.isCollaborator
    }

    canManageCompany(id: number) {
        if (!this.canManageCompanies) return false

        if (this.isCollaborator) {
            return this.editableCompanies?.some((companyId) => companyId === id) ?? false
        }

        return true
    }

    get canManageMeters() {
        return this.isAdmin || this.isCollaborator
    }

    canManageMetersByCompany(id: number | string) {
        if (!this.canManageMeters) return false

        if (this.isCollaborator) {
            const idInternal = typeof id === 'number' ? id : parseInt(id)
            return this.editableCompanies?.some((companyId) => companyId === idInternal) ?? false
        }

        return true
    }

    get canManageFactories() {
        return this.canManageMeters
    }

    canManageFactoriesByCompany(id: number | string) {
        return this.canManageMetersByCompany(id)
    }

    get canSeeMeterContracts() {
        return this.isAdmin || this.isCollaborator
    }

    get canSeeMeterSettings() {
        return this.isAdmin || this.isCollaborator
    }

    canSeeMeterSettingsByCompany(id: number | string) {
        if (!this.canSeeMeterSettings) return false

        if (this.isCollaborator) {
            const idInternal = typeof id === 'number' ? id : parseInt(id)
            return this.editableCompanies?.some((companyId) => companyId === idInternal) ?? false
        }
        return true
    }

    get canManageMeterSettings() {
        // TODO collaborator only his company
        return this.isAdmin || this.isCollaborator
    }

    get canDownloadMeterCosts() {
        return this.isAdmin || this.isCollaborator
    }

    canDownloadMeterCostsByCompany(id: number | string) {
        if (!this.canDownloadMeterCosts) return false

        if (this.isCollaborator) {
            const idInternal = typeof id === 'number' ? id : parseInt(id)
            return this.editableCompanies?.some((companyId) => companyId === idInternal) ?? false
        }

        return true
    }

    get canManageMeterCosts() {
        return this.isAdmin || this.isCollaborator
    }

    canManageMeterCostsByCompany(id: number | string) {
        if (!this.canManageMeterCosts) return false

        if (this.isCollaborator) {
            const idInternal = typeof id === 'number' ? id : parseInt(id)
            return this.editableCompanies?.some((companyId) => companyId === idInternal) ?? false
        }

        return true
    }
}

export type UserFormUpdate = {
    name?: string
    surname?: string
    business_name?: string
    companies_id?: number[]
    holding_id?: number[]
}
